import { CookiesName, setCookie } from '@/utils/cookies'
import { initField } from '@/utils/storeInput'
import type { Field } from '@/utils/storeInput'
import { validateEmail } from '@/utils/validations'
import { defineStore } from 'pinia'

import Api from '@/services/api'

const requestErrorKeys = ['invalid_credentials', 'invalid_token', 'non_compliant_password']

interface State {
  email: Field<string>
  password: Field<string>
  confirmationPassword: Field<string>
  requestError: string | null
  token: string | null
  remember: boolean
}

const initialState = (): State => ({
  email: initField(localStorage.getItem('userEmail') ? (localStorage.getItem('userEmail') as string) : ''),
  password: initField(''),
  confirmationPassword: initField(''),
  requestError: null,
  token: null,
  remember: false
})

export const useAuthFormStore = defineStore({
  id: 'auth-form',
  state: () => initialState(),
  getters: {
    isFormValid(): boolean {
      return !this.email.error && !!this.email.value && !!this.password.value
    }
  },
  actions: {
    setEmail(email: string) {
      this.email.value = email
      this.email.error = validateEmail(email) ? null : 'auth_forms.fields_errors.invalid_email'
    },
    setPassword(password: string) {
      this.password.value = password
      this.password.error = password.length ? null : 'auth_forms.fields_errors.missing_field'
    },
    setRemember(value: boolean) {
      this.remember = value
    },
    setConfirmationPassword(password: string) {
      this.confirmationPassword.value = password
      this.confirmationPassword.error = !password.length
        ? 'auth_forms.fields_errors.missing_field'
        : password !== this.$state.password.value
        ? 'auth_forms.fields_errors.invalid_password_match'
        : null
    },
    setRequestError(error: string | null) {
      if (error) this.requestError = requestErrorKeys.includes(error) ? error : 'unknown'
      else this.requestError = null
    },
    disconnect() {
      this.token = null
      this.password = initField('')
    },
    async loginRequest() {
      localStorage.setItem('userEmail', this.email.value)
      return Api.login(this.email.value, this.password.value)
        .then((response) => {
          this.token = response.data.token
          if (this.remember) {
            setCookie(CookiesName.Token, this.token, 7)
          }
          return { success: true }
        })
        .catch((e) => {
          this.setRequestError(e?.response?.data?.message || 'error')
          return { success: false }
        })
    }
  }
})
